import React from "react";

const StickyHeaderContext = React.createContext<{
  headerHeight: number;
  enabled: boolean;
  scrolled: boolean;
}>({ headerHeight: 0, enabled: false, scrolled: false });

const StickyHeaderProvider = ({
  headerRef,
  children
}: {
  headerRef: React.RefObject<HTMLDivElement> | null;
  children: React.ReactNode;
}) => {
  const enabled = !!headerRef?.current;
  const [headerHeight, setHeaderHeight] = React.useState(0);
  const [scrolled, setScrolled] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!headerRef?.current) {
      return;
    }

    const header = headerRef.current;
    const measure = () => {
      setHeaderHeight(header.getBoundingClientRect().height || 0);
    };
    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(header);

      return () => {
        observer.disconnect();
      };
    }

    return () => {};
  }, [headerRef]);

  React.useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    // Created an invisible marker element positioned at the top of the viewport
    const marker = document.createElement("div");
    Object.assign(marker.style, {
      position: "absolute",
      top: "10px",
      height: "1px",
      width: "1px",
      opacity: "0",
      pointerEvents: "none",
      zIndex: "-1"
    });
    document.body.appendChild(marker);

    // Replaced window scroll event listener with Intersection Observer to avoid continuous scroll event handling
    // This Observer detects when the marker is no longer visible, which indicates the user has scrolled down
    const observer = new IntersectionObserver(([entry]) => {
      setScrolled(!entry.isIntersecting);
    });
    observer.observe(marker);

    return () => {
      observer.disconnect();
      if (document.body.contains(marker)) {
        document.body.removeChild(marker);
      }
    };
  }, [enabled]);

  return (
    <StickyHeaderContext.Provider value={{ headerHeight, enabled, scrolled }}>
      {children}
    </StickyHeaderContext.Provider>
  );
};

const useStickyHeader = () => {
  const context = React.useContext(StickyHeaderContext);
  if (!context) {
    throw new Error(
      "useStickyHeader must be used within a StickyHeaderProvider"
    );
  }
  return context;
};

export { StickyHeaderProvider, useStickyHeader };
